import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import {
  User, Mail, MapPin, Lock, CreditCard, Shield, Bell, Package,
  Eye, EyeOff, Save, Edit, X, CheckCircle, AlertCircle, Calendar,
  DollarSign, Smartphone, Key, History, Users, Plus,
  Trash2, RefreshCw, ExternalLink, Settings as SettingsIcon,
  ChevronRight, ChevronDown, ChevronUp, LogOut, Info, ArrowRight
} from 'lucide-react';
import api from '../../utils/api';
import { callAuthPostWithFallback, __internal as authFallbackInternal } from '../../utils/authFallback';
import {
  NOTIFICATION_DEFAULTS,
  NOTIFICATION_TOGGLE_CONFIG,
  getNotificationMeta,
  normalizeNotificationPreferences
} from '../../constants/notificationPreferences';
import { extractBusinessProfile } from '../../utils/contactNormalization';
import { COUNTRIES } from '../../utils/countries';

// Initialize Stripe - must be at module level for use in PaymentMethodSetupModal
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_51S13rxBnBT7CggSVggNOdJmGCBnUmZOdV3z5psTElNtVIBIJC1kJWsjbBwDxUG35yhjwAeR7bJk431J578QNABRQ008FawJFLT');

const PRO_PLAN_FEATURES = [
  'Chat functionality',
  'Up to 5 user accounts',
  'Advanced analytics',
  'Priority support',
  'All Essential features'
];

const ESSENTIAL_PLAN_FEATURES = [
  'Send and receive B2B invoices',
  'Automatic invoice approval (7-day rule)',
  'Automatic payment collection',
  'Track payment statuses',
  'Analytics dashboard',
  '1 user account'
];

const PRO_PLAN_FEATURE_PREVIEW = PRO_PLAN_FEATURES.slice(0, 4);
const ESSENTIAL_PLAN_FEATURE_PREVIEW = ESSENTIAL_PLAN_FEATURES.slice(0, 3);

const normalizePlan = (plan) => {
  const normalized = String(plan || '').toLowerCase();
  if (normalized === 'pro') return 'pro';
  if (normalized === 'none' || normalized === 'no plan') return 'none';
  return 'essential';
};

const formatPlanLabel = (plan) => {
  const normalized = normalizePlan(plan);
  if (normalized === 'pro') return 'Pro';
  if (normalized === 'none') return 'No Plan';
  return 'Essential';
};

const formatSubscriptionStatus = (status) => {
  if (!status) {
    return 'Unknown';
  }

  const normalized = String(status).toLowerCase();
  const statusMap = {
    active: 'Active',
    pending: 'Pending',
    cancelled: 'Cancelled',
    canceled: 'Cancelled',
    inactive: 'Inactive',
    past_due: 'Past Due',
    unpaid: 'Unpaid',
    paused: 'Paused',
    suspended: 'Suspended',
    trialing: 'Trialing',
    free_trial: 'Trialing'
  };

  if (statusMap[normalized]) {
    return statusMap[normalized];
  }

  return normalized
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getPlanPrice = (plan) => {
  const normalized = normalizePlan(plan);
  if (normalized === 'pro') return 49.99;
  if (normalized === 'none') return 0;
  return 39.99;
};

const getPlanFeatures = (plan) => {
  const normalized = normalizePlan(plan);
  if (normalized === 'pro') {
    return [...PRO_PLAN_FEATURES];
  }
  if (normalized === 'none') {
    return [];
  }
  return [...ESSENTIAL_PLAN_FEATURES];
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP'
  }).format(value || 0);

const formatDateDisplay = (isoString) => {
  if (!isoString) return null;
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const MobileSettings = () => {
  const {
    user,
    logout,
    notificationPreferences: initialNotificationPreferences,
    setNotificationPreferences,
    setNotificationDelivery,
    updateProfile
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [expandedSections, setExpandedSections] = useState({
    paymentMethod: false,
    subscription: true,
    account: false,
    payment: false,
    notifications: false,
    twoFactor: false,
    loginHistory: false,
    users: false,
    password: false
  });

  // Check if user is main account holder (additional users have role === 'additional_user')
  const isMainAccountHolder = user?.role !== 'additional_user';
  const normalizedUserStatus = String(user?.status || '').toLowerCase();
  const normalizedSubscriptionStatus = String(user?.subscriptionStatus || '').toLowerCase();
  const isInactiveAccount =
    normalizedUserStatus === 'inactive' ||
    normalizedSubscriptionStatus === 'cancelled';

  // Account Info State - Build from user data like desktop
  const buildAccountInfo = useCallback(
    (sourceUser) => {
      const businessProfile = extractBusinessProfile(sourceUser);
      return {
        firstName: sourceUser?.firstName || '',
        lastName: sourceUser?.lastName || '',
        email: sourceUser?.email || '',
        companyName: sourceUser?.companyName || '',
        phone: sourceUser?.phone || '',
        address: {
          street: businessProfile.address || '',
          city: businessProfile.city || '',
          state: businessProfile.state || '',
          zipCode: businessProfile.zipCode || '',
          country: businessProfile.country || 'United Kingdom'
        }
      };
    },
    []
  );

  const [accountInfo, setAccountInfo] = useState(() => buildAccountInfo(user));
  const [isEditingAccount, setIsEditingAccount] = useState(false);

  // Update accountInfo when user changes (but not when editing)
  useEffect(() => {
    if (!user || isEditingAccount) {
      return;
    }
    setAccountInfo(buildAccountInfo(user));
  }, [user, isEditingAccount, buildAccountInfo]);

  const [subscription, setSubscription] = useState({
    plan: formatPlanLabel(user?.subscriptionPlan),
    price: getPlanPrice(user?.subscriptionPlan),
    status: user?.subscriptionStatus || 'active',
    nextBilling: user?.subscriptionEndDate || null,
    trialEnds: user?.subscriptionTrialEnds || user?.subscriptionTrialEndDate || null,
    features: getPlanFeatures(user?.subscriptionPlan),
    lastPlan: formatPlanLabel(user?.subscriptionPlan)
  });
  const [planChangeModal, setPlanChangeModal] = useState({
    open: false,
    plan: null,
    confirming: false
  });
  const [cancelModal, setCancelModal] = useState({
    open: false,
    confirming: false
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const [additionalUsers, setAdditionalUsers] = useState([]);
  const [additionalUsersLoading, setAdditionalUsersLoading] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: ''
  });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [invitingUser, setInvitingUser] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);
  const [resendingUserId, setResendingUserId] = useState(null);

  const isProPlan = useMemo(
    () => normalizePlan(subscription.plan) === 'pro',
    [subscription.plan]
  );
  const isSubscriptionCancelled = useMemo(
    () => String(subscription.status || '').toLowerCase() === 'cancelled',
    [subscription.status]
  );

  const loadAdditionalUsers = useCallback(async () => {
    if (!isMainAccountHolder || isInactiveAccount) {
      setAdditionalUsers([]);
      setAdditionalUsersLoading(false);
      return;
    }

    setAdditionalUsersLoading(true);
    try {
      const response = await api.get('/users/additional-users');
      if (response.data?.success) {
        setAdditionalUsers(response.data.data || []);
      } else {
        console.warn('[MobileSettings] Failed to load additional users', response.data);
      }
    } catch (error) {
      console.error('[MobileSettings] Error loading additional users:', error);
      setMessage({
        type: 'error',
        text: 'Unable to load additional users. Please try again later.'
      });
    } finally {
      setAdditionalUsersLoading(false);
    }
  }, [isMainAccountHolder, isInactiveAccount]);

  useEffect(() => {
    setSubscription(prev => ({
      ...prev,
      plan: formatPlanLabel(user?.subscriptionPlan),
      price: getPlanPrice(user?.subscriptionPlan),
      status: user?.subscriptionStatus || prev.status,
      nextBilling: user?.subscriptionEndDate || null,
      trialEnds: user?.subscriptionTrialEnds || user?.subscriptionTrialEndDate || null,
      features: getPlanFeatures(user?.subscriptionPlan),
      lastPlan: formatPlanLabel(user?.subscriptionPlan || prev.lastPlan)
    }));
  }, [
    user?.subscriptionPlan,
    user?.subscriptionStatus,
    user?.subscriptionEndDate,
    user?.subscriptionTrialEnds,
    user?.subscriptionTrialEndDate
  ]);

  useEffect(() => {
    if (normalizePlan(subscription.plan) !== 'pro' || isInactiveAccount) {
      setShowAddUserForm(false);
    }
  }, [subscription.plan, isInactiveAccount]);

  // Password State
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState(false);

  // Two-Factor Setup State
  const [twoFactorSetupLoading, setTwoFactorSetupLoading] = useState(false);
  const [twoFactorModalOpen, setTwoFactorModalOpen] = useState(false);
  const [twoFactorSetupData, setTwoFactorSetupData] = useState(null);
  const [twoFactorConfirmCode, setTwoFactorConfirmCode] = useState('');
  const [twoFactorConfirmError, setTwoFactorConfirmError] = useState('');
  const [twoFactorReconfiguring, setTwoFactorReconfiguring] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ reconfigureRequired: false });
  const twoFactorPortalRef = useRef(null);

  const handleStartTwoFactorSetup = async () => {
    // Allow both main users and additional users to set up their own 2FA
    const wasEnabled = Boolean(security.twoFactor.enabled);
    const hasCompletedInitialSetup = Boolean(security.twoFactor.hasCompletedInitialSetup);
    const enteringReconfigure = wasEnabled || hasCompletedInitialSetup;
    setTwoFactorReconfiguring(enteringReconfigure);

    setTwoFactorSetupLoading(true);
    setTwoFactorModalOpen(false);
    setTwoFactorSetupData(null);
    setTwoFactorConfirmCode('');
    setTwoFactorConfirmError('');
    try {
      let data;
    try {
        const apiResponse = await api.post('/api/users/two-factor/setup', {}, { __skipAuthTokenPurge: true });
        data = apiResponse.data;
      } catch (primaryError) {
        console.warn('[MobileSettings] 2FA setup via API proxy failed, using auth lambda fallback', primaryError);
        data = await callAuthPostWithFallback(api, {
          apiPath: '/users/two-factor/setup',
        fallbackPath: '/users/two-factor/setup',
          authMode: 'query',
          forceFallback: true,
          allowFallbackOnProduction: true,
      });
      }

      if (data?.success) {
        setTwoFactorSetupData(data.data);
        setTwoFactorModalOpen(true);
        window.dispatchEvent(new CustomEvent('twofactor:updated'));
        window.dispatchEvent(new CustomEvent('twofactor:mobile-status', {
          detail: { reconfigureRequired: enteringReconfigure }
        }));
        const nextMessage = enteringReconfigure
          ? 'Two-factor reconfiguration started. Scan the new QR code and verify the 6-digit code to continue using Receevit®.'
          : 'Two-factor setup started. Scan the QR code to enable added protection.';
        setMessage({ type: 'info', text: nextMessage });
        // Reload security data to reflect pending state
        if (loadSecurityData) {
          await loadSecurityData().catch((err) =>
            console.error('Error refreshing security data after starting 2FA setup (mobile):', err)
          );
        }
      } else {
        throw new Error(data?.error || 'Setup failed');
      }
    } catch (error) {
      console.error('Error starting 2FA setup:', error);
      const errorText =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to initiate two-factor setup. Please try again on desktop.';
      setMessage({ type: 'error', text: errorText });
    } finally {
      setTwoFactorSetupLoading(false);
    }
  };

  const handleConfirmTwoFactorSetup = async () => {
    if (!twoFactorConfirmCode || twoFactorConfirmCode.trim().length < 6) {
      setTwoFactorConfirmError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    try {
      setTwoFactorConfirmError('');
      setTwoFactorSetupLoading(true);

      let data;
      try {
        const apiResponse = await api.post(
          '/api/users/two-factor/verify',
          {
            code: twoFactorConfirmCode.trim(),
          },
          {
            __skipAuthTokenPurge: true
          }
        );
        data = apiResponse.data;
      } catch (primaryError) {
        console.warn('[MobileSettings] 2FA verify via API proxy failed, using auth lambda fallback', primaryError);
        data = await callAuthPostWithFallback(api, {
          apiPath: '/users/two-factor/verify',
          fallbackPath: '/users/two-factor/verify',
          payload: {
            code: twoFactorConfirmCode.trim(),
          },
          authMode: 'query',
          forceFallback: true,
          allowFallbackOnProduction: true,
        });
      }

      if (data?.success) {
        const verifiedAt = new Date().toISOString();
        setSecurity(prev => ({
          ...prev,
          twoFactor: {
            ...prev.twoFactor,
            enabled: true,
            status: 'enabled',
            gracePeriodEndsAt: prev.twoFactor.gracePeriodEndsAt,
            daysRemaining: 0,
            verifiedAt,
            firstVerifiedAt: prev.twoFactor.firstVerifiedAt || verifiedAt,
            hasCompletedInitialSetup: true,
            reconfigureRequired: false
          }
        }));
        if (loadSecurityData) {
          await loadSecurityData().catch((err) =>
            console.error('Error refreshing security data after verifying 2FA (mobile):', err)
          );
        }
        setTwoFactorModalOpen(false);
        setTwoFactorSetupData(null);
        setTwoFactorConfirmCode('');
        setTwoFactorReconfiguring(false);
        setMessage({ type: 'success', text: 'Two-factor authentication enabled successfully!' });
        window.dispatchEvent(new CustomEvent('twofactor:updated'));
        window.dispatchEvent(new CustomEvent('twofactor:mobile-status', {
          detail: { reconfigureRequired: false }
        }));
      } else {
        setTwoFactorConfirmError(data?.error || 'Verification failed. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying two-factor code:', error);
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        'Verification failed. Please try again.';
      setTwoFactorConfirmError(message);
    } finally {
      setTwoFactorSetupLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    if (!twoFactorSetupData?.backupCodes) {
      return;
    }

    const blob = new Blob(
      [
        'Receevit® Two-Factor Authentication Backup Codes\n\n',
        ...twoFactorSetupData.backupCodes.map(code => `${code}\n`),
        '\nStore these codes in a safe place. Each code can be used once.',
      ],
      { type: 'text/plain;charset=utf-8' }
    );

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'receevit-backup-codes.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Stripe Connect State
  const [stripeAccountStatus, setStripeAccountStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(true); // Start as loading to prevent flash of "No Stripe Connected"
  const [stripePaymentInfo, setStripePaymentInfo] = useState(null);
  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);

  const isSandboxHost =
    typeof window !== 'undefined' &&
    window.location?.hostname &&
    window.location.hostname.includes('sandbox');

  const [notifications, setNotifications] = useState(() =>
    normalizeNotificationPreferences(initialNotificationPreferences)
  );
  useEffect(() => {
    setNotifications(normalizeNotificationPreferences(initialNotificationPreferences));
  }, [initialNotificationPreferences]);
  const [notificationLoading, setNotificationLoading] = useState(false);

  const [security, setSecurity] = useState({
    twoFactor: {
      enabled: false,
      status: 'pending',
      gracePeriodEndsAt: null,
      daysRemaining: null,
      verifiedAt: null,
      firstVerifiedAt: null,
      hasCompletedInitialSetup: false,
      reconfigureRequired: false
    },
    loginHistory: []
  });
  const [securityLoading, setSecurityLoading] = useState(false);

  const toggleSection = (section) => {
    if (isInactiveAccount && section !== 'subscription') {
      return;
    }
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  useEffect(() => {
    if (isInactiveAccount) {
      setExpandedSections({
        subscription: true,
        account: false,
        payment: false,
        notifications: false,
        twoFactor: false,
        loginHistory: false,
        users: false,
        password: false
      });
    }
  }, [isInactiveAccount]);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    if (!twoFactorPortalRef.current) {
      const existing = document.getElementById('twofactor-portal-root');
      if (existing) {
        twoFactorPortalRef.current = existing;
      } else {
        const node = document.createElement('div');
        node.id = 'twofactor-portal-root';
        node.dataset.twofactorPortalRoot = 'true';
        twoFactorPortalRef.current = node;
      }
    }

    const portalNode = twoFactorPortalRef.current;
    if (portalNode && !portalNode.isConnected) {
      document.body.appendChild(portalNode);
    }

    return () => {
      if (
        portalNode &&
        portalNode.parentNode &&
        portalNode.dataset.twofactorPortalRoot === 'true' &&
        portalNode.childElementCount === 0
      ) {
        portalNode.parentNode.removeChild(portalNode);
      }
    };
  }, []);

  const renderTwoFactorModal = () => {
    if (!twoFactorModalOpen) {
      return null;
    }

    const modalContent = (
      <div
        data-twofactor-modal
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4 py-6"
      >
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-5 max-h-[90vh] overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              setTwoFactorModalOpen(false);
              setTwoFactorSetupData(null);
              setTwoFactorConfirmCode('');
              setTwoFactorConfirmError('');
              setTwoFactorSetupLoading(false);
            }}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="space-y-5">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Set up Two-Factor Authentication</h3>
              <p className="mt-2 text-sm text-gray-600">
                Scan the QR code with Google Authenticator, Microsoft Authenticator, or any compatible app. Then enter the 6-digit code below.
              </p>
            </div>

            <div className="flex justify-center">
              {twoFactorSetupData?.otpauth ? (
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFactorSetupData.otpauth)}`}
                  alt="Scan to set up two-factor authentication"
                  className="rounded-xl border border-gray-200 shadow-sm"
                />
              ) : (
                <div className="w-[200px] h-[200px] bg-gray-100 rounded-xl animate-pulse" />
              )}
            </div>

            {twoFactorSetupData?.secret && (
              <div>
                <p className="text-xs uppercase text-gray-500 font-semibold">Manual entry code</p>
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-2 bg-gray-100 text-gray-800 rounded-lg font-mono text-sm tracking-widest">
                  {twoFactorSetupData.secret}
                </div>
              </div>
            )}

            {twoFactorSetupData?.backupCodes && (
              <div>
                <div className="flex items-center justify-between">
                  <p className="text-xs uppercase text-gray-500 font-semibold">Backup codes</p>
                  <button
                    type="button"
                    onClick={downloadBackupCodes}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Download codes
                  </button>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {twoFactorSetupData.backupCodes.map((code) => (
                    <div
                      key={code}
                      className="px-3 py-2 bg-white border border-gray-200 rounded-lg font-mono text-sm tracking-widest text-gray-700"
                    >
                      {code}
                    </div>
                  ))}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Store these in a safe place. Each backup code can be used once if you lose access to your authenticator app.
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Enter the 6-digit code
              </label>
              <input
                type="text"
                value={twoFactorConfirmCode}
                onChange={(e) => setTwoFactorConfirmCode(e.target.value.replace(/[^0-9A-Za-z-]/g, ''))}
                inputMode="numeric"
                autoComplete="one-time-code"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center text-lg tracking-[0.4em]"
                placeholder="••••••"
              />
              {twoFactorConfirmError && (
                <p className="mt-2 text-sm text-red-600">{twoFactorConfirmError}</p>
              )}

              <button
                type="button"
                onClick={handleConfirmTwoFactorSetup}
                disabled={twoFactorSetupLoading || !twoFactorConfirmCode}
                className="mt-6 w-full inline-flex items-center justify-center px-4 py-3 border border-transparent rounded-lg text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {twoFactorSetupLoading ? 'Verifying…' : 'Verify & Enable'}
              </button>

              <p className="mt-4 text-xs text-gray-500 text-center">
                Once verified, two-factor authentication will be enabled immediately for your account.
              </p>
            </div>
          </div>
        </div>
      </div>
    );

    if (twoFactorPortalRef.current) {
      return createPortal(modalContent, twoFactorPortalRef.current);
    }

    console.warn('[MobileSettings] Portal container missing — rendering inline fallback.');
    return modalContent;
  };

  const handleAccountSave = async () => {
    setLoading(true);
    try {
      // Flatten address object to match backend schema (like desktop)
      const updateData = {
        firstName: accountInfo.firstName,
        lastName: accountInfo.lastName,
        companyName: accountInfo.companyName,
        phone: accountInfo.phone,
        // Flatten address fields to match database columns
        address: accountInfo.address.street,
        city: accountInfo.address.city,
        state: accountInfo.address.state,
        zipCode: accountInfo.address.zipCode,
        country: accountInfo.address.country
      };
      
      console.log('💾 Saving account info:', updateData);
      
      // Use direct Lambda Function URL to bypass API Gateway's WAF issues with JWT tokens
      const token = localStorage.getItem('token');
      const lambdaResponse = await fetch('https://jihcfjgfzbcof7fsii3ks4zceq0zibvh.lambda-url.eu-west-2.on.aws/', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-http-method': 'PUT',
          'x-http-path': '/profile'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!lambdaResponse.ok) {
        const errorData = await lambdaResponse.json();
        throw new Error(errorData.error || 'Failed to update profile');
      }
      
      setIsEditingAccount(false);
      setMessage({ type: 'success', text: 'Account information updated successfully!' });
      
      // Refresh user data
      window.location.reload();
    } catch (error) {
      console.error('❌ Failed to save account info:', error);
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update account information' });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    setLoading(true);
    try {
      await callAuthPostWithFallback(api, {
        apiPath: '/users/change-password',
        fallbackPath: '/users/change-password',
        method: 'put',
        payload: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        allowFallbackOnProduction: true,
        forceFallback: true,
        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL
      });
      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update password' });
    } finally {
      setLoading(false);
    }
  };

  const openPlanChangeModal = (plan) => {
    setPlanChangeModal({
      open: true,
      plan,
      confirming: false
    });
  };

  const handleSubscriptionChange = async (newPlan) => {
    setSubscriptionLoading(true);
    setMessage({ type: '', text: '' });
    setPlanChangeModal(prev => ({ ...prev, confirming: true }));

    try {
      const response = await callAuthPostWithFallback(api, {
        apiPath: '/subscription/change-plan',
        fallbackPath: '/subscription/change-plan',
        method: 'post',
        payload: {
          newPlan: newPlan.toLowerCase()
        },
        requireAuthToken: true,
        allowFallbackOnProduction: true,
        forceFallback: true,
        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL,
        additionalHeaders: {
          'Content-Type': 'application/json'
        }
      });

      if (response?.success) {
        const normalizedPlan = normalizePlan(newPlan);
        const planLabel = formatPlanLabel(newPlan);
        const updatedSubscription = response.subscription || {};
        const normalizedStatus = updatedSubscription.status || 'active';
        const nextBillingDate = updatedSubscription.current_period_end
          ? new Date(updatedSubscription.current_period_end * 1000).toISOString()
          : subscription.nextBilling;
        const trialEndsAt = updatedSubscription.trial_end
          ? new Date(updatedSubscription.trial_end * 1000).toISOString()
          : subscription.trialEnds;

        setSubscription(prev => ({
          ...prev,
          plan: planLabel,
          price: getPlanPrice(newPlan),
          status: normalizedStatus,
          features: getPlanFeatures(newPlan),
          nextBilling: nextBillingDate,
          trialEnds: trialEndsAt,
          lastPlan: planLabel
        }));

        await updateProfile({
          subscriptionPlan: normalizedPlan,
          subscriptionStatus: normalizedStatus,
          status: 'active',
          subscriptionEndDate: nextBillingDate,
          subscriptionTrialEnds: trialEndsAt
        });

        await loadAdditionalUsers();

        window.dispatchEvent(new CustomEvent('subscriptionChanged', {
          detail: {
            plan: normalizedPlan,
            status: normalizedStatus
          }
        }));

        setMessage({
          type: 'success',
          text: `✅ ${response.subscription?.changeType || 'Plan updated'} successfully! Your plan is now ${planLabel}.`
        });
      } else {
        const responseMessage = response?.message || response?.error;
        switch (response?.code) {
          case 'STRIPE_SUBSCRIPTION_REQUIRES_ACTION':
            setMessage({
              type: 'warning',
              text: responseMessage || 'Stripe needs additional authorisation to complete this change. Please check your bank app and try again.'
            });
            break;
          case 'STRIPE_SUBSCRIPTION_REQUIRES_PAYMENT_METHOD':
            setMessage({
              type: 'error',
              text: responseMessage || 'We could not charge your saved payment method. Please update the card in Stripe and retry.'
            });
            break;
          case 'STRIPE_CUSTOMER_NOT_FOUND':
            setMessage({
              type: 'error',
              text: 'We could not find your Stripe customer record. Please contact support so we can reconnect your billing.'
            });
            break;
          case 'PLAN_CHANGE_REQUIRES_STRIPE_INVITE':
            setMessage({
              type: 'warning',
              text: responseMessage || 'Your Stripe account needs to be reconnected before changing plans.'
            });
            break;
          default:
            setMessage({
              type: 'error',
              text: responseMessage || 'Failed to change plan. Please try again or contact support.'
            });
        }
      }
    } catch (error) {
      const errorData = error.response?.data || {};
      const errorMessage =
        errorData.message ||
        errorData.error ||
        error.message ||
        'Failed to change subscription. Please try again or contact support.';

      switch (errorData.code) {
        case 'STRIPE_SUBSCRIPTION_REQUIRES_ACTION':
          setMessage({
            type: 'warning',
            text: errorMessage
          });
          break;
        case 'STRIPE_SUBSCRIPTION_REQUIRES_PAYMENT_METHOD':
          setMessage({
            type: 'error',
            text: errorMessage
          });
          break;
        case 'STRIPE_CUSTOMER_NOT_FOUND':
          setMessage({
            type: 'error',
            text: 'We could not find your Stripe customer record. Please contact support so we can reconnect your billing.'
          });
          break;
        case 'PLAN_CHANGE_REQUIRES_STRIPE_INVITE':
          setMessage({
            type: 'warning',
            text: errorMessage
          });
          break;
        default:
          setMessage({
            type: 'error',
            text: errorMessage
          });
      }
    } finally {
      setSubscriptionLoading(false);
      setPlanChangeModal({ open: false, plan: null, confirming: false });
    }
  };

  const handleAddUser = async () => {
    if (normalizePlan(subscription.plan) !== 'pro') {
      setMessage({
        type: 'info',
        text: 'Additional users are available on the Pro plan. Upgrade to Pro to invite your team.'
      });
      return;
    }

    if (!newUserForm.name.trim() || !newUserForm.email.trim()) {
      setMessage({ type: 'error', text: 'Please enter both the name and email.' });
      return;
    }

    if (additionalUsers.length >= 4) {
      setMessage({ type: 'error', text: 'You can add up to 4 additional users on the Pro plan.' });
      return;
    }

    setInvitingUser(true);
    try {
      const response = await api.post('/users/additional-users/invite', {
        name: newUserForm.name.trim(),
        email: newUserForm.email.trim(),
        companyName: user?.companyName || 'Receevit®',
        inviterName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email,
        invitationMessage:
          `You've been invited to join ${user?.companyName || 'Receevit®'} on Receevit®. Click the link below to accept your invitation and set up your account.`
      });

      if (response.data?.success) {
        setMessage({ type: 'success', text: 'User invitation sent successfully!' });
        setNewUserForm({ name: '', email: '' });
        setShowAddUserForm(false);
        await loadAdditionalUsers();
      } else {
        setMessage({ type: 'error', text: response.data?.error || 'Failed to send invite. Please try again.' });
      }
    } catch (error) {
      console.error('[MobileSettings] Failed to invite user:', error);
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to send invite. Please try again.'
      });
    } finally {
      setInvitingUser(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    setRemovingUserId(userId);
    try {
      await api.delete(`/users/additional-users/${userId}`);
      setMessage({ type: 'success', text: 'User removed successfully.' });
      await loadAdditionalUsers();
    } catch (error) {
      console.error('[MobileSettings] Failed to remove user:', error);
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to remove user. Please try again.'
      });
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleResendInvite = async (userId) => {
    setResendingUserId(userId);
    try {
      await api.post(`/users/additional-users/${userId}/resend`);
      setMessage({ type: 'success', text: 'Invitation resent successfully!' });
    } catch (error) {
      console.error('[MobileSettings] Failed to resend invite:', error);
      setMessage({
        type: 'error',
        text: error?.response?.data?.message || 'Failed to resend invite. Please try again.'
      });
    } finally {
      setResendingUserId(null);
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeModal.plan) {
      return;
    }

    await handleSubscriptionChange(planChangeModal.plan);
  };

  const handleCancelSubscription = async () => {
    setCancelModal(prev => ({ ...prev, confirming: true }));
    try {
      const response = await callAuthPostWithFallback(api, {
        apiPath: '/subscription/cancel',
        fallbackPath: '/subscription/cancel',
        method: 'post',
        requireAuthToken: true,
        allowFallbackOnProduction: true,
        forceFallback: true,
        payload: {},
        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL
      });

      if (response?.success) {
        setSubscription(prev => ({
          ...prev,
          plan: 'No Plan',
          price: 0,
          features: [],
          status: 'cancelled',
          nextBilling: null,
          trialEnds: null,
          lastPlan: response.subscription?.planName || prev.lastPlan || formatPlanLabel(prev.plan)
        }));

        await updateProfile({
          subscriptionStatus: 'cancelled',
          subscriptionPlan: 'essential',
          status: 'inactive'
        });

        await loadAdditionalUsers();

        window.dispatchEvent(new CustomEvent('subscriptionChanged', {
          detail: {
            plan: 'essential',
            status: 'cancelled'
          }
        }));

        setMessage({
          type: 'success',
          text: 'Subscription cancelled successfully. You can reactivate anytime from here.'
        });
      } else {
        setMessage({
          type: 'error',
          text: response?.error || 'Failed to cancel subscription. Please try again.'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          'Failed to cancel subscription. Please try again.'
      });
    } finally {
      setCancelModal({ open: false, confirming: false });
    }
  };

  const handleManagePaymentMethods = async () => {
    setSubscriptionLoading(true);
    try {
      const response = await callAuthPostWithFallback(api, {
        apiPath: '/stripe-connect/create-portal-session',
        fallbackPath: '/stripe-connect/create-portal-session',
        method: 'post',
        payload: {
          returnUrl: window.location.href
        },
        allowFallbackOnProduction: true,
        forceFallback: true,
        fallbackBaseUrlOverride: authFallbackInternal.AUTH_LAMBDA_FUNCTION_URL
      });

      if (response?.success && response?.url) {
        window.location.href = response.url;
      } else {
        setMessage({
          type: 'error',
          text: response?.error || 'Failed to open payment portal'
        });
      }
    } catch (error) {
      console.error('Error opening payment portal:', error);
      setMessage({
        type: 'error',
        text: 'Failed to open payment portal'
      });
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const renderPlanChangeModal = () => {
    if (!planChangeModal.open || !planChangeModal.plan) {
      return null;
    }

    const targetPlan = planChangeModal.plan;
    const normalizedTarget = normalizePlan(targetPlan);
    const isDowngrade = normalizedTarget === 'essential' && !isInactiveAccount;
    const isUpgrade = normalizedTarget === 'pro';
    const modalTitle = isDowngrade
      ? 'Confirm downgrade'
      : isInactiveAccount
        ? 'Confirm reactivation'
        : isUpgrade
          ? 'Confirm upgrade'
          : 'Confirm plan change';

    const description = isDowngrade
      ? 'You\'re about to downgrade to the Essential plan. Pro-only features like chat, advanced analytics, and additional team seats will be disabled immediately.'
      : isInactiveAccount
        ? `You're about to reactivate on the ${targetPlan} plan. Your saved card will be charged right away and access to Receevit® features is restored instantly.`
        : 'You\'re about to upgrade to the Pro plan. Pro features unlock immediately and Stripe charges the difference straight away.';

    const bulletPoints = isDowngrade
      ? [
          'Chat and multi-user access will be disabled immediately.',
          'Stripe issues a prorated credit for unused Pro time.',
          'You can upgrade back to Pro at any time.'
        ]
      : isInactiveAccount
        ? [
            'Your team regains access immediately after reactivation.',
            'The saved card is charged right away for the new cycle.',
            'Future billing resumes on the new plan schedule.'
          ]
        : [
            'Pro features unlock instantly after upgrade.',
            'Stripe charges the difference immediately.',
            'You can downgrade back to Essential whenever you need.'
          ];

    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{modalTitle}</h3>
              <p className="mt-2 text-sm text-gray-600">{description}</p>
            </div>
            <button
              onClick={() => setPlanChangeModal({ open: false, plan: null, confirming: false })}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <ul className="mt-4 space-y-2 text-sm text-gray-600">
            {bulletPoints.map((item, index) => (
              <li key={index} className="flex items-start space-x-2">
                <span className="mt-1 inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-blue-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          <div className="mt-6 space-y-2">
            <button
              onClick={handleConfirmPlanChange}
              disabled={subscriptionLoading}
              className="w-full inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {subscriptionLoading ? 'Updating…' : 'Yes, change my plan'}
            </button>
            <button
              onClick={() => setPlanChangeModal({ open: false, plan: null, confirming: false })}
              disabled={subscriptionLoading}
              className="w-full inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Keep current plan
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCancelModal = () => {
    if (!cancelModal.open) {
      return null;
    }

    return (
      <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 px-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Cancel subscription</h3>
              <p className="mt-2 text-sm text-gray-600">
                Cancelling immediately downgrades your account and your team loses access to Receevit® features. You can reactivate anytime.
              </p>
            </div>
            <button
              onClick={() => setCancelModal({ open: false, confirming: false })}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start space-x-2">
              <span className="mt-1 inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
              <span>Pro and Essential features are disabled right away.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="mt-1 inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
              <span>Scheduled automation, chat, and team access stop immediately.</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="mt-1 inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-red-500" />
              <span>You can reactivate later and restore your data without losing history.</span>
            </li>
          </ul>

          <div className="space-y-2">
            <button
              onClick={handleCancelSubscription}
              disabled={cancelModal.confirming}
              className="w-full inline-flex items-center justify-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {cancelModal.confirming ? 'Cancelling…' : 'Yes, cancel my subscription'}
            </button>
            <button
              onClick={() => setCancelModal({ open: false, confirming: false })}
              disabled={cancelModal.confirming}
              className="w-full inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Keep subscription
            </button>
          </div>
        </div>
      </div>
    );
  };

  const checkStripeAccountStatus = async () => {
    try {
      const response = await api.get('/api/stripe-connect/account-status', {
        timeout: 10000 // 10 second timeout
      });
      setStripeAccountStatus(response.data.account);
      setStripeLoading(false);
    } catch (error) {
      console.error('Error checking Stripe account status:', error);
      
      // Handle timeout gracefully - show helpful message instead of "No account"
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        console.warn('Stripe account status check timed out');
        setStripeAccountStatus({ 
          timeout: true, 
          message: 'Unable to verify account status. If you recently connected Stripe, your account is likely active. Check your Stripe dashboard to confirm.',
          helpLink: 'https://dashboard.stripe.com/connect/accounts'
        });
        setStripeLoading(false);
      } else if (error.response?.status === 404) {
        // No account found - this is expected for new users
        setStripeAccountStatus(null);
        setStripeLoading(false);
      } else if (error.code === 'NETWORK_ERROR' || !error.response || error.response?.status >= 500) {
        // Network/server error - show helpful message
        console.warn('Server temporarily unavailable');
        setStripeAccountStatus({ 
          timeout: true, 
          message: 'Unable to verify account status due to server timeout. Your Stripe account may be connected. Please check your Stripe dashboard.',
          helpLink: 'https://dashboard.stripe.com/connect/accounts'
        });
        setStripeLoading(false);
      } else {
        // Other errors
        setMessage({ type: 'error', text: 'Failed to check Stripe account status' });
        setStripeAccountStatus(null);
        setStripeLoading(false);
      }
    }
  };

  const linkStripeAccount = async () => {
    setStripeLoading(true);
    setMessage({ type: 'info', text: 'Preparing Stripe Connect...' });

    try {
      // Check if account is already fully set up
      if (stripeAccountStatus && stripeAccountStatus.chargesEnabled && stripeAccountStatus.payoutsEnabled) {
        window.open('https://dashboard.stripe.com/connect/accounts', '_blank');
        setMessage({ type: 'success', text: 'Opening Stripe dashboard...' });
        return;
      }

      // Account needs setup, try to get onboarding link
      let response;
      try {
        response = await api.post('/api/stripe-connect/onboarding-link');
      } catch (error) {
        if (error.response?.status === 404 && error.response?.data?.error === 'No Stripe Connect account found') {
          response = await api.post('/api/stripe-connect/create-account', {
            country: 'GB',
            businessType: 'individual'
          });
          response = await api.post('/api/stripe-connect/onboarding-link');
        } else {
          throw error;
        }
      }

      const redirectUrl = response.data.accountLink || response.data.url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        throw new Error('No redirect URL received from server');
      }
    } catch (error) {
      console.error('Error linking Stripe account:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to create onboarding link' 
      });
    } finally {
      setStripeLoading(false);
    }
  };

  const loadSecurityData = async () => {
    setSecurityLoading(true);
    try {
      const response = await api.get('/api/users/security');
      if (response.data?.success) {
        const data = response.data.data;
        const twoFactorData = data?.twoFactor || {};
        const normalizedTwoFactor = {
          enabled: Boolean(twoFactorData.enabled),
          status: twoFactorData.status || 'pending',
          gracePeriodEndsAt: twoFactorData.gracePeriodEndsAt || null,
          daysRemaining: Number.isFinite(twoFactorData.daysRemaining) ? twoFactorData.daysRemaining : null,
          verifiedAt: twoFactorData.verifiedAt || null,
          firstVerifiedAt: twoFactorData.firstVerifiedAt || null,
          hasCompletedInitialSetup: Boolean(twoFactorData.hasCompletedInitialSetup),
          reconfigureRequired: Boolean(twoFactorData.reconfigureRequired)
        };

        setSecurity({
          twoFactor: normalizedTwoFactor,
          loginHistory: Array.isArray(data?.loginHistory) ? data.loginHistory : []
        });
        setTwoFactorReconfiguring(normalizedTwoFactor.reconfigureRequired);
        setTwoFactorStatus({ reconfigureRequired: normalizedTwoFactor.reconfigureRequired });
        window.dispatchEvent(new CustomEvent('twofactor:mobile-status', {
          detail: { reconfigureRequired: normalizedTwoFactor.reconfigureRequired }
        }));
      }
    } catch (error) {
      console.error('Error loading security data:', error);
    } finally {
      setSecurityLoading(false);
    }
  };

  const loadNotificationPreferences = async () => {
    if (!isMainAccountHolder) {
      return;
    }

    setNotificationLoading(true);
    try {
      const response = await api.get('/users/notification-preferences');
      if (response.data?.success) {
        const prefs = response.data.data || {};
        const normalized = normalizeNotificationPreferences(prefs);
        setNotifications(normalized);
        setNotificationPreferences(normalized);
        localStorage.setItem('notification_preferences', JSON.stringify(normalized));
        const delivery = response.data.deliveryMethods || {};
        setNotificationDelivery({
          email: delivery.email !== false,
          inApp: delivery.inApp !== false,
          push: delivery.push === true
        });
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleNotificationToggle = async (key) => {
    if (!isMainAccountHolder) {
      return;
    }

    const meta = getNotificationMeta(key);
    const label = meta?.label || key.replace(/([A-Z])/g, ' $1').trim();

    const updatedPreferences = {
      ...notifications,
      [key]: !notifications[key]
    };

    setNotifications(updatedPreferences);

    try {
      await callAuthPostWithFallback(api, {
        apiPath: '/users/notification-preferences',
        fallbackPath: '/users/notification-preferences',
        method: 'put',
        authMode: 'query',
        payload: {
        preferences: updatedPreferences,
        deliveryMethods: {
          email: true,
          inApp: true,
          push: false
        },
        userEmail: user?.email,
        userName: user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email
        }
      });

      if (key === 'chatMessages') {
        window.dispatchEvent(
          new CustomEvent('chatNotificationPreferenceChanged', {
            detail: { enabled: updatedPreferences[key] }
          })
        );
      }

      setNotificationPreferences(updatedPreferences);
      localStorage.setItem('notification_preferences', JSON.stringify(updatedPreferences));
      setNotificationDelivery({
        email: true,
        inApp: true,
        push: false
      });

      setMessage({
        type: 'success',
        text: `${label} notifications ${updatedPreferences[key] ? 'enabled' : 'disabled'}.`
      });
    } catch (error) {
      console.error('Failed to update notification preference:', error);
      setNotifications(prev => ({
        ...prev,
        [key]: !prev[key]
      }));
      setMessage({ type: 'error', text: `Failed to update ${label.toLowerCase()} notifications` });
    }
  };

  useEffect(() => {
    const initialize = async () => {
      await Promise.all([
        checkStripeAccountStatus(),
        (async () => {
          try {
            const paymentData = await api.get('/api/users/payment-info');
            if (paymentData.data.success) {
              setStripePaymentInfo(paymentData.data.paymentInfo);
            }
          } catch (error) {
            console.error('Error loading Stripe payment info:', error);
          }
        })(),
        (async () => {
          if (isMainAccountHolder) {
            try {
              const response = await api.get('/api/payment-methods/default');
              if (response.data.success) {
                setDefaultPaymentMethod(response.data.data);
              }
            } catch (error) {
              if (error.response?.status === 404) {
                // Check if payment method was deleted (not just missing)
                if (error.response?.data?.code === 'PAYMENT_METHOD_DELETED' || 
                    error.response?.data?.code === 'CUSTOMER_DELETED') {
                  // Payment method or customer was deleted from Stripe
                  setDefaultPaymentMethod(null);
                  setMessage({ 
                    type: 'error', 
                    text: 'Your payment method was removed from Stripe. Please add a new payment method to continue making payments.' 
                  });
                  console.warn('Payment method was deleted from Stripe:', error.response?.data?.message);
                }
                // Otherwise, 404 is expected if no payment method is set
              } else {
                console.error('Error loading default payment method:', error);
              }
            }
          }
        })(),
        loadNotificationPreferences(),
        loadSecurityData(),
        loadAdditionalUsers()
      ]);
    };

    initialize();

    const handleTwoFactorUpdated = () => {
      loadSecurityData();
    };

    const handleTwoFactorStatus = (event) => {
      if (event?.detail && typeof event.detail.reconfigureRequired === 'boolean') {
        setTwoFactorStatus({ reconfigureRequired: event.detail.reconfigureRequired });
      }
    };

    window.addEventListener('twofactor:updated', handleTwoFactorUpdated);
    window.addEventListener('twofactor:mobile-status', handleTwoFactorStatus);

    return () => {
      window.removeEventListener('twofactor:updated', handleTwoFactorUpdated);
      window.removeEventListener('twofactor:mobile-status', handleTwoFactorStatus);
    };
  }, [loadAdditionalUsers]);

  useEffect(() => {
    loadAdditionalUsers();
  }, [loadAdditionalUsers]);

  useEffect(() => {
    if (security.twoFactor.enabled) {
      setTwoFactorReconfiguring(false);
    }
  }, [security.twoFactor.enabled]);

  const formatDateTime = (isoString) => {
    if (!isoString) return 'Unknown time';
    try {
      return new Date(isoString).toLocaleString();
    } catch (_) {
      return isoString;
    }
  };

  const formatLoginType = (type) => {
    if (!type) return 'login';
    if (type === 'staff_login') return 'Staff Login';
    if (type === 'client_login') return 'Client Login';
    return type.replace(/_/g, ' ');
  };

  const formatFailureReason = (reason) => {
    if (!reason) return 'Authentication failure';
    return reason.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  };

  const SettingSection = ({ title, icon: Icon, children, isExpanded, onToggle }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <Icon className="w-5 h-5 text-[#1F11ED]" />
          <span className="font-semibold text-gray-900">{title}</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2 }}
          className="border-t border-gray-100"
        >
          {children}
        </motion.div>
      )}
    </div>
  );

  return (
    <>
      {renderTwoFactorModal()}
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm p-4">
        <h1 className="text-xl font-bold text-gray-900">Settings</h1>
      </div>

        {/* Security Alerts */}
        {twoFactorStatus.reconfigureRequired && (
          <div className="m-4 rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-600 mt-1" />
              <div className="text-left">
                <h2 className="text-sm font-semibold text-blue-900">
                  Reconfigure two-factor authentication to continue
                </h2>
                <p className="text-sm text-blue-800 mt-1">
                  You previously completed 2FA. For your security you must reconfigure it now to keep using Receevit®.
                </p>
                <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside mt-2">
                  <li>Open your authenticator app</li>
                  <li>Scan the new QR code shown below</li>
                  <li>Enter the 6-digit code to confirm</li>
                </ul>
              </div>
            </div>
          </div>
        )}
        {!twoFactorStatus.reconfigureRequired && security.twoFactor &&
          security.twoFactor.enabled !== true &&
          (typeof security.twoFactor.daysRemaining === 'number' && security.twoFactor.daysRemaining <= 0) && (
            <div className="m-4 rounded-xl border border-blue-200 bg-blue-50 p-4 shadow-sm">
              <div className="flex items-start space-x-3">
                <Shield className="w-5 h-5 text-blue-600 mt-1" />
                <div className="text-left">
                  <h2 className="text-sm font-semibold text-blue-900">
                    Set up two-factor authentication to continue
                  </h2>
                  <p className="text-sm text-blue-800 mt-1">
                    Your grace period has ended. Complete two-factor authentication now to keep your Receevit® account protected.
                  </p>
                  <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside mt-2">
                    <li>Press "Set up 2FA" in the Security section below</li>
                    <li>Scan the QR code with your authenticator</li>
                    <li>Enter the 6-digit code to finish the setup</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
      {message.text && (
          <div
            className={`p-4 mx-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800'
                : message.type === 'info'
                ? 'bg-blue-50 text-blue-800'
                : 'bg-red-50 text-red-800'
            }`}
          >
          <div className="flex items-center space-x-2">
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
              ) : message.type === 'info' ? (
                <Info className="w-5 h-5" />
            ) : (
              <AlertCircle className="w-5 h-5" />
            )}
            <span>{message.text}</span>
          </div>
        </div>
      )}

      {isInactiveAccount && (
        <div className="m-4 space-y-3">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <div className="flex items-start space-x-3">
              <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600">
                <AlertCircle className="h-5 w-5" />
              </span>
              <div className="space-y-1 text-left">
                <h2 className="text-base font-semibold text-amber-900">
                  Your subscription is inactive
                </h2>
                <p className="text-sm text-amber-800">
                  Reactivate your plan to regain access to invoices, chat, payments, and team features. Until then, only the Subscription tab is available.
                </p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                onClick={() => openPlanChangeModal('Pro')}
                className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-700"
              >
                Upgrade to Pro now
              </button>
              <button
                onClick={() => openPlanChangeModal('Essential')}
                className="inline-flex items-center justify-center rounded-lg border border-amber-300 px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100"
              >
                Reactivate on Essential
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Limited Access Notice for Additional Users */}
      {!isInactiveAccount && !isMainAccountHolder && (
        <div className="m-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-yellow-800">Additional User Account</h3>
              <p className="text-sm text-yellow-700">
                You have limited access to settings. Only the main account holder can manage subscriptions, Stripe Connect, notifications, and team members.
              </p>
              <p className="text-xs text-yellow-600 mt-2">
                You can manage your own password and two-factor authentication.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* Subscription - Only for main account holder */}
        {isMainAccountHolder && (
        <SettingSection
          title="Subscription"
          icon={Package}
          isExpanded={expandedSections.subscription}
          onToggle={() => toggleSection('subscription')}
        >
          <div className="p-4 space-y-4">
            {isInactiveAccount && (
              <div className="space-y-3">
                <div className="rounded-2xl border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-white p-4 shadow-sm space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1 text-left">
                      <span className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700">
                        Pro Plan · £49.99/month
                      </span>
                      <h4 className="text-lg font-bold text-gray-900">Unlock everything Receevit® offers</h4>
                      <p className="text-sm text-gray-600">
                        Reactivate with Pro to regain chat, advanced analytics, and multi-user access immediately.
                      </p>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-indigo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                      Recommended
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {PRO_PLAN_FEATURE_PREVIEW.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    <li className="flex items-center space-x-2 text-sm text-blue-600">
                      <ArrowRight className="h-4 w-4" />
                      <span>+ everything in Essential</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => openPlanChangeModal('Pro')}
                    disabled={subscriptionLoading}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                  >
                    {subscriptionLoading && planChangeModal.open && planChangeModal.plan === 'Pro'
                      ? 'Processing...'
                      : 'Upgrade to Pro now'}
                  </button>
                </div>

                <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                    Essential Plan · £39.99/month
                  </span>
                  <h4 className="text-lg font-semibold text-gray-900">Keep things streamlined</h4>
                  <p className="text-sm text-gray-600">
                    Perfect if you're not ready for Pro yet—reactivate your core invoicing and automation tools.
                  </p>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {ESSENTIAL_PLAN_FEATURE_PREVIEW.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => openPlanChangeModal('Essential')}
                    disabled={subscriptionLoading}
                    className="inline-flex w-full items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {subscriptionLoading && planChangeModal.open && planChangeModal.plan === 'Essential'
                      ? 'Processing...'
                      : 'Reactivate on Essential'}
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="text-left space-y-1">
                  <p className="text-xs font-semibold uppercase text-gray-500">Current Plan</p>
                  <h4 className="text-lg font-bold text-gray-900">
                    {formatPlanLabel(subscription.plan)}{formatPlanLabel(subscription.plan) === 'No Plan' ? '' : ' Plan'}
                  </h4>
                  <p className="text-xl font-bold text-blue-600">
                    {formatPlanLabel(subscription.plan) === 'No Plan'
                      ? '—'
                      : `${formatCurrency(subscription.price)}/month`}
                  </p>
                  {subscription.nextBilling && !isSubscriptionCancelled && (
                    <p className="text-xs text-gray-500">
                      Next billing: {formatDateDisplay(subscription.nextBilling)}
                    </p>
                  )}
                  {isSubscriptionCancelled && subscription.lastPlan && (
                    <p className="text-xs text-gray-500">
                      Last active plan: {subscription.lastPlan}
                    </p>
                  )}
                </div>
                <span
                  className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                    subscription.status === 'active'
                      ? 'bg-green-100 text-green-800'
                      : isSubscriptionCancelled
                      ? 'bg-red-100 text-red-600'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {formatSubscriptionStatus(subscription.status)}
                </span>
              </div>

              <div className="space-y-2">
                <h5 className="text-sm font-semibold text-gray-700">Plan features</h5>
                {subscription.features.length > 0 ? (
                  <ul className="space-y-1.5 text-sm text-gray-600">
                    {subscription.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircle className="mt-[2px] h-4 w-4 text-green-500" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">Reactivate to regain access to Receevit® features.</p>
                )}
              </div>

              <div className="space-y-2">
                <button
                  onClick={handleManagePaymentMethods}
                  disabled={subscriptionLoading}
                  className="w-full inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {subscriptionLoading ? 'Opening portal…' : 'Manage payment methods'}
                </button>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(!isProPlan || isInactiveAccount) && (
                    <button
                      onClick={() => openPlanChangeModal('Pro')}
                      disabled={subscriptionLoading}
                      className="inline-flex items-center justify-center rounded-lg border border-transparent bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {subscriptionLoading && planChangeModal.open && planChangeModal.plan === 'Pro'
                        ? 'Processing…'
                        : 'Upgrade to Pro'}
                    </button>
                  )}
                  {!isInactiveAccount && isProPlan && (
                    <button
                      onClick={() => openPlanChangeModal('Essential')}
                      disabled={subscriptionLoading}
                      className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {subscriptionLoading && planChangeModal.open && planChangeModal.plan === 'Essential'
                        ? 'Processing…'
                        : 'Downgrade to Essential'}
                    </button>
                  )}
                </div>
                {!isSubscriptionCancelled && (
                  <button
                    onClick={() => setCancelModal({ open: true, confirming: false })}
                    disabled={subscriptionLoading}
                    className="w-full inline-flex items-center justify-center rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    Cancel subscription
                  </button>
                )}
              </div>
            </div>
          </div>
        </SettingSection>
        )}

        {/* Account Information */}
        {!isInactiveAccount && (
        <SettingSection
          title="Account Information"
          icon={User}
          isExpanded={expandedSections.account}
          onToggle={() => toggleSection('account')}
        >
          <div className="p-4 space-y-4">
            {/* Additional users see read-only view, no edit button */}
            {!isMainAccountHolder && (
              <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Your account information is managed by your administrator. Please contact them to update your details.
                </p>
              </div>
            )}
            
            {!isEditingAccount ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium">{accountInfo.firstName} {accountInfo.lastName}</p>
                  </div>
                  {/* Only show Edit button for main account holders */}
                  {isMainAccountHolder && (
                    <button
                      onClick={() => setIsEditingAccount(true)}
                      className="p-2 text-[#1F11ED] hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{accountInfo.email}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Company</p>
                  <p className="font-medium">{accountInfo.companyName}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{accountInfo.phone || 'Not set'}</p>
                </div>
                
                <div>
                  <p className="text-sm text-gray-600">Address</p>
                  <p className="font-medium">
                    {accountInfo.address.street || 'Not set'}
                    {accountInfo.address.city && `, ${accountInfo.address.city}`}
                    {accountInfo.address.zipCode && ` ${accountInfo.address.zipCode}`}
                    {accountInfo.address.country && `, ${accountInfo.address.country}`}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      value={accountInfo.firstName}
                      onChange={(e) => setAccountInfo(prev => ({ ...prev, firstName: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={accountInfo.lastName}
                      onChange={(e) => setAccountInfo(prev => ({ ...prev, lastName: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={accountInfo.email}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={accountInfo.companyName}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={accountInfo.phone}
                    onChange={(e) => setAccountInfo(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                  />
                </div>
                
                {/* Address Fields */}
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Business Address</h4>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                      <input
                        type="text"
                        value={accountInfo.address.street}
                        onChange={(e) => setAccountInfo(prev => ({
                          ...prev,
                          address: { ...prev.address, street: e.target.value }
                        }))}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                        placeholder="123 Business Street"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                        <input
                          type="text"
                          value={accountInfo.address.city}
                          onChange={(e) => setAccountInfo(prev => ({
                            ...prev,
                            address: { ...prev.address, city: e.target.value }
                          }))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                          placeholder="London"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                        <input
                          type="text"
                          value={accountInfo.address.zipCode}
                          onChange={(e) => setAccountInfo(prev => ({
                            ...prev,
                            address: { ...prev.address, zipCode: e.target.value }
                          }))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                          placeholder="SW1A 1AA"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">State/County</label>
                        <input
                          type="text"
                          value={accountInfo.address.state}
                          onChange={(e) => setAccountInfo(prev => ({
                            ...prev,
                            address: { ...prev.address, state: e.target.value }
                          }))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                          placeholder="England (optional)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                        <select
                          value={accountInfo.address.country}
                          onChange={(e) => setAccountInfo(prev => ({
                            ...prev,
                            address: { ...prev.address, country: e.target.value }
                          }))}
                          className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                        >
                          {COUNTRIES.map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleAccountSave}
                    disabled={loading}
                    className="flex-1 bg-[#1F11ED] text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setIsEditingAccount(false)}
                    className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </SettingSection>
        )}

        {/* Password & Security */}
        {!isInactiveAccount && (
        <SettingSection
          title="Password"
          icon={Lock}
          isExpanded={expandedSections.password}
          onToggle={() => toggleSection('password')}
        >
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <div className="relative">
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  className="w-full p-2 pr-10 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
                />
                <button
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-[#1F11ED] focus:border-[#1F11ED]"
              />
            </div>
            
            <button
              onClick={handlePasswordChange}
              disabled={loading || !passwordData.currentPassword || !passwordData.newPassword}
              className="w-full bg-[#1F11ED] text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50 inline-flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </div>
        </SettingSection>
        )}

        {/* Stripe Connected Account - Only for main account holder */}
        {!isInactiveAccount && isMainAccountHolder && (
          <SettingSection
            title="Stripe Connected Account"
            icon={CreditCard}
            isExpanded={expandedSections.payment}
            onToggle={() => toggleSection('payment')}
          >
          <div className="p-4 space-y-4">
            {stripeAccountStatus ? (
              <div className="space-y-4">
                {/* Main Status Card - Mobile Optimized */}
                {stripeAccountStatus.chargesEnabled && stripeAccountStatus.payoutsEnabled ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <h4 className="font-medium text-green-900 text-sm">✅ Stripe Payments Ready</h4>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center space-x-2">
                        <span className="text-base">💰</span>
                        <span className="text-green-800 font-medium text-sm">You can RECEIVE payments</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-base">💳</span>
                        <span className="text-green-800 font-medium text-sm">You can MAKE payments</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={() => window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}`, '_blank')}
                        className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Stripe Connected Account
                      </button>
                      <button
                        onClick={() => setShowAccountDetails(!showAccountDetails)}
                        className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center"
                      >
                        {showAccountDetails ? 'Hide Details' : 'View Account IDs'} 
                        {showAccountDetails ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                      <h4 className="font-medium text-yellow-900 text-sm">⚠️ Stripe Setup Required</h4>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="text-yellow-800">
                        <p className="font-medium text-sm mb-1">To RECEIVE payments, you need to:</p>
                        <ul className="list-disc list-inside space-y-1 text-xs ml-2">
                          <li>Complete Stripe account setup</li>
                          <li>Add bank account details</li>
                        </ul>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-base">💳</span>
                        <span className="text-yellow-800 font-medium text-sm">You can already MAKE payments</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <button
                        onClick={linkStripeAccount}
                        disabled={stripeLoading}
                        className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center disabled:opacity-50"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {stripeLoading ? 'Connecting...' : 'Complete Setup'}
                      </button>
                      <button
                        onClick={() => setShowAccountDetails(!showAccountDetails)}
                        className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center"
                      >
                        {showAccountDetails ? 'Hide Details' : 'View Account IDs'} 
                        {showAccountDetails ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
                      </button>
                    </div>
                  </div>
                )}

                {/* Progressive Disclosure - Account Details - Mobile Optimized */}
                {showAccountDetails && (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                    <h5 className="font-medium text-gray-900 text-sm">📋 Account Information</h5>
                    
                    <div className="space-y-4">
                      {/* Receiving Account - Mobile */}
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-base">🏦</span>
                          <h6 className="font-medium text-gray-900 text-sm">Receiving Account</h6>
                        </div>
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-gray-500 uppercase tracking-wide">Account ID</label>
                            <div className="flex items-center space-x-1 mt-1">
                              <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded flex-1 break-all">
                                {stripeAccountStatus.stripeAccountId}
                              </span>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(stripeAccountStatus.stripeAccountId);
                                  setMessage({ type: 'success', text: 'Account ID copied!' });
                                }}
                                className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                              >
                                Copy
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-gray-600">Type:</span>
                              <span className="ml-1 font-medium">Standard</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Country:</span>
                              <span className="ml-1 font-medium">GB</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Charges:</span>
                              <span className={`ml-1 font-medium ${stripeAccountStatus.chargesEnabled ? 'text-green-600' : 'text-red-600'}`}>
                                {stripeAccountStatus.chargesEnabled ? '✅' : '❌'}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-600">Payouts:</span>
                              <span className={`ml-1 font-medium ${stripeAccountStatus.payoutsEnabled ? 'text-green-600' : 'text-red-600'}`}>
                                {stripeAccountStatus.payoutsEnabled ? '✅' : '❌'}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}`, '_blank')}
                            className="w-full text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded"
                          >
                            View in Stripe Dashboard
                          </button>
                          <p className="text-xs text-gray-500">
                            💡 For getting paid by clients
                          </p>
                        </div>
                      </div>

                      {/* Customer Account - Mobile */}
                      {stripePaymentInfo && stripePaymentInfo.stripeCustomerId && (
                        <div className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-base">💳</span>
                            <h6 className="font-medium text-gray-900 text-sm">Customer Account</h6>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-gray-500 uppercase tracking-wide">Customer ID</label>
                              <div className="flex items-center space-x-1 mt-1">
                                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded flex-1 break-all">
                                  {stripePaymentInfo.stripeCustomerId}
                                </span>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(stripePaymentInfo.stripeCustomerId);
                                    setMessage({ type: 'success', text: 'Customer ID copied!' });
                                  }}
                                  className="text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded"
                                >
                                  Copy
                                </button>
                              </div>
                            </div>
                            <div className="space-y-1 text-xs">
                              <div>
                                <span className="text-gray-600">Status:</span>
                                <span className="ml-1 font-medium text-green-600">✅ Active</span>
                              </div>
                              <div>
                                <span className="text-gray-600">Email:</span>
                                <span className="ml-1 font-medium text-xs break-all">{stripePaymentInfo.userEmail}</span>
                              </div>
                            </div>
                            <button
                              onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripePaymentInfo.stripeCustomerId}`, '_blank')}
                              className="w-full text-xs bg-blue-100 hover:bg-blue-200 text-blue-800 px-2 py-1 rounded"
                            >
                              View in Stripe Dashboard
                            </button>
                            <p className="text-xs text-gray-500">
                              💡 For making payments to others
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Auto-collect toggle removed – backend always collects on due dates */}
              </div>
            ) : stripeLoading ? (
              <div className="space-y-4">
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="text-center py-6">
                    <RefreshCw className="w-6 h-6 mx-auto animate-spin text-blue-600" />
                    <p className="mt-3 text-sm font-medium text-gray-900">Loading Stripe account status...</p>
                    <p className="mt-1 text-xs text-gray-500">Please wait while we check your account</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-800 mb-2">Connect Stripe Account</h3>
                  <p className="text-sm text-blue-700 mb-3">
                    Connect your Stripe account to enable automatic payment collection on invoice due dates.
                  </p>
                  
                  <button
                    onClick={linkStripeAccount}
                    disabled={stripeLoading}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium disabled:opacity-50"
                  >
                    <CreditCard className="w-4 h-4 inline mr-2" />
                    {stripeLoading ? 'Connecting...' : 'Connect Stripe Account'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </SettingSection>
        )}

        {/* Payment Method Management Section - Mobile */}
        {!isInactiveAccount && stripeAccountStatus && stripeAccountStatus.chargesEnabled && isMainAccountHolder && (
          <SettingSection
            title="Payment Method for Invoices"
            icon={CreditCard}
            isExpanded={expandedSections.paymentMethod}
            onToggle={() => toggleSection('paymentMethod')}
          >
            <div className="p-4 space-y-4">
              {paymentMethodLoading ? (
                <div className="text-center py-6">
                  <RefreshCw className="w-5 h-5 mx-auto animate-spin text-gray-400" />
                  <p className="mt-2 text-xs text-gray-600">Loading payment method...</p>
                </div>
              ) : defaultPaymentMethod ? (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <div className="flex-1">
                        <h4 className="font-medium text-green-900 text-sm">Default Payment Method Set</h4>
                        <p className="text-xs text-green-700 mt-1">
                          {defaultPaymentMethod.paymentMethod?.card ? (
                            <>
                              {defaultPaymentMethod.paymentMethod.card.brand.toUpperCase()} •••• {defaultPaymentMethod.paymentMethod.card.last4}
                              {' - Expires '}
                              {defaultPaymentMethod.paymentMethod.card.expMonth.toString().padStart(2, '0')}/{defaultPaymentMethod.paymentMethod.card.expYear}
                            </>
                          ) : (
                            'Payment method configured'
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <button
                      onClick={async () => {
                        setPaymentMethodLoading(true);
                        try {
                          if (stripeAccountStatus?.stripeAccountId && defaultPaymentMethod?.stripeCustomerId) {
                            // Open directly to the customer's payment methods page on the connected account
                            window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}/customers/${defaultPaymentMethod.stripeCustomerId}`, '_blank');
                            setMessage({ type: 'info', text: 'Opened Stripe Dashboard to your customer\'s payment methods. You can add, remove, or update payment methods here.' });
                          } else if (stripeAccountStatus?.stripeAccountId) {
                            // If no customer ID, open to the customers list
                            window.open(`https://dashboard.stripe.com/connect/accounts/${stripeAccountStatus.stripeAccountId}/customers`, '_blank');
                            setMessage({ type: 'info', text: 'Opened Stripe Dashboard. Go to Customers → select your customer → Payment Methods tab to manage payment methods.' });
                          } else {
                            setMessage({ type: 'error', text: 'Stripe account not found. Please set up your Stripe Connect account first.' });
                          }
                        } catch (error) {
                          setMessage({ type: 'error', text: 'Failed to open Stripe dashboard' });
                        } finally {
                          setPaymentMethodLoading(false);
                        }
                      }}
                      className="w-full bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Manage Payment Methods
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm('Remove your default payment method? You will need to set one up before sending invoices.')) {
                          return;
                        }
                        setPaymentMethodLoading(true);
                        try {
                          await api.delete('/api/payment-methods/default');
                          setDefaultPaymentMethod(null);
                          setMessage({ type: 'success', text: 'Payment method removed' });
                        } catch (error) {
                          setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to remove payment method' });
                        } finally {
                          setPaymentMethodLoading(false);
                        }
                      }}
                      className="w-full bg-white border border-red-300 text-red-700 py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove Payment Method
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Used for "Pay Now" and "Pay on Due Date" when paying invoices.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                      <div className="flex-1">
                        <h4 className="font-medium text-yellow-900 text-sm">No Payment Method Set</h4>
                        <p className="text-xs text-yellow-700 mt-1">
                          Set up a payment method before sending invoices. This will be used for paying invoices.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowPaymentMethodModal(true);
                    }}
                    className="w-full bg-blue-600 text-white py-2 px-3 rounded-lg font-medium text-sm flex items-center justify-center"
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Set Up Payment Method
                  </button>
                </div>
              )}
            </div>
          </SettingSection>
        )}

        {/* Notifications - Only for main account holder */}
        {!isInactiveAccount && isMainAccountHolder && (
          <SettingSection
            title="Notifications"
            icon={Bell}
            isExpanded={expandedSections.notifications}
            onToggle={() => toggleSection('notifications')}
          >
            <div className="p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Notification Preferences</h3>

              {notificationLoading ? (
                <p className="text-sm text-gray-500">Loading notification preferences...</p>
              ) : (
                <div className="space-y-3">
                  {NOTIFICATION_TOGGLE_CONFIG.map(({ key, label, description }) => {
                    const value = notifications[key] ?? NOTIFICATION_DEFAULTS[key];
                    return (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700">{label}</p>
                          <p className="text-xs text-gray-500">{description}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => handleNotificationToggle(key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#1F11ED]"></div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </SettingSection>
        )}

        {!isInactiveAccount && (
          <SettingSection
            id="twofactor-section"
            title="Two-Factor Authentication"
            icon={Shield}
            isExpanded={expandedSections.twoFactor}
            onToggle={() => toggleSection('twoFactor')}
          >
            <div className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <p className="font-medium">
                    {security.twoFactor.enabled
                      ? 'Enabled'
                      : (security.twoFactor.reconfigureRequired || twoFactorReconfiguring)
                      ? 'Reconfiguration required'
                      : 'Pending'}
                  </p>
                </div>
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                    security.twoFactor.enabled
                      ? 'bg-green-100 text-green-700'
                      : security.twoFactor.reconfigureRequired || twoFactorReconfiguring
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}
                >
                  {security.twoFactor.enabled
                    ? 'Enabled'
                    : (security.twoFactor.reconfigureRequired || twoFactorReconfiguring)
                    ? 'Reconfigure'
                    : 'Pending'}
                </span>
              </div>
              {!security.twoFactor.enabled && (
                <div className="space-y-2">
                  <p
                    className={`text-sm rounded-lg px-3 py-2 flex items-center gap-2 ${
                      security.twoFactor.reconfigureRequired || twoFactorReconfiguring
                        ? 'text-blue-800 bg-blue-50 border border-blue-200'
                        : security.twoFactor.daysRemaining > 0
                        ? 'text-yellow-700 bg-yellow-50 border border-yellow-200'
                        : 'text-red-700 bg-red-50 border border-red-200'
                    }`}
                  >
                    <AlertCircle className="w-4 h-4" />
                    {security.twoFactor.reconfigureRequired || twoFactorReconfiguring
                      ? 'Two-factor authentication needs to be reconfigured. Scan the new QR code and verify the 6-digit code now to continue using Receevit®.'
                      : security.twoFactor.daysRemaining > 0
                      ? `Set up two-factor authentication within the next ${security.twoFactor.daysRemaining} day${security.twoFactor.daysRemaining === 1 ? '' : 's'}.`
                      : 'Two-factor authentication is required. Please set it up now to keep access.'}
                  </p>
                  {security.twoFactor.reconfigureRequired && (
                    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-2">
                      <h4 className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                        Reconfigure now to keep access
                      </h4>
                      <p className="text-sm text-blue-800">
                        You previously completed two-factor authentication. For your security you must reconfigure it now to continue using Receevit®.
                      </p>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside text-left">
                        <li>Open your authenticator app.</li>
                        <li>Scan the new QR code shown below.</li>
                        <li>Enter the 6-digit code to confirm.</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
              {security.twoFactor.enabled && security.twoFactor.verifiedAt && (
                <p className="text-sm text-gray-500">
                  Verified on {formatDateTime(security.twoFactor.verifiedAt)}
                </p>
              )}
              {security.twoFactor.gracePeriodEndsAt &&
                !security.twoFactor.enabled &&
                !twoFactorReconfiguring &&
                !security.twoFactor.reconfigureRequired && (
                  <p className="text-xs text-gray-500">
                    Grace period ends {formatDateTime(security.twoFactor.gracePeriodEndsAt)}
                  </p>
              )}
              <button
                onClick={handleStartTwoFactorSetup}
                disabled={twoFactorSetupLoading}
                className="w-full bg-[#1F11ED] text-white py-3 rounded-lg font-medium disabled:opacity-50 inline-flex items-center justify-center"
              >
                {twoFactorSetupLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Preparing...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    {security.twoFactor.enabled ? 'Reconfigure 2FA' : 'Set up 2FA'}
                  </>
                )}
              </button>
            </div>
          </SettingSection>
        )}

        {/* Login History - Only for main account holders */}
        {!isInactiveAccount && isMainAccountHolder && (
          <SettingSection
            title="Login History"
            icon={History}
            isExpanded={expandedSections.loginHistory}
            onToggle={() => toggleSection('loginHistory')}
          >
            <div className="p-4 space-y-3">
              {securityLoading ? (
                <p className="text-sm text-gray-500">Loading login activity...</p>
              ) : security.loginHistory.length === 0 ? (
                <p className="text-sm text-gray-500">No recent logins recorded yet.</p>
              ) : (
                security.loginHistory.slice(0, 15).map((log, index) => {
                  const success = log.success !== false;
                  const statusBadgeClass = success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700';
                  const IconComponent = success ? CheckCircle : AlertCircle;
                  return (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-100 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${success ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                            <IconComponent className="w-3 h-3" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{log.userName || log.device}</p>
                            {log.userEmail && <p className="text-xs text-gray-500">{log.userEmail}</p>}
                          </div>
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass}`}>
                          {success ? 'Successful' : 'Failed'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 space-y-1">
                        <p>{formatLoginType(log.loginType)} • {log.device}</p>
                        <p>{log.location}</p>
                        <p>IP: {log.ip}</p>
                        <p>{formatDateTime(log.date)}</p>
                        {!success && log.failureReason && (
                          <p className="text-red-500">Reason: {formatFailureReason(log.failureReason)}</p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </SettingSection>
        )}

        {!isInactiveAccount && isMainAccountHolder && (
          <SettingSection
            title="Additional Users"
            icon={Users}
            isExpanded={expandedSections.users}
            onToggle={() => toggleSection('users')}
          >
            <div className="p-4 space-y-4">
              {normalizePlan(subscription.plan) !== 'pro' ? (
                <div className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 space-y-2 text-left">
                  <div className="flex items-center space-x-2">
                    <Package className="h-5 w-5 text-yellow-600" />
                    <h4 className="text-sm font-semibold text-yellow-900">Upgrade to Pro to add teammates</h4>
                  </div>
                  <p className="text-sm text-yellow-800">
                    Additional users are included with the Pro plan (£49.99/month). Upgrade to invite up to 4 team members to your Receevit® workspace.
                  </p>
                  <button
                    onClick={() => openPlanChangeModal('Pro')}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-yellow-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-700"
                  >
                    <Package className="mr-2 h-4 w-4" />
                    Upgrade to Pro
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold text-gray-900">Invite teammates</h4>
                      <p className="text-sm text-gray-600">Add up to 4 additional users to collaborate on invoices, chat, and payments.</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>Users: {additionalUsers.length}/4</span>
                        {additionalUsers.length >= 4 && (
                          <span className="text-xs font-semibold text-red-600">Maximum reached</span>
                        )}
                      </div>
                      <div className="h-2 w-full rounded-full bg-gray-200">
                        <div
                          className={`h-2 rounded-full ${additionalUsers.length >= 4 ? 'bg-red-500' : 'bg-blue-500'}`}
                          style={{ width: `${Math.min(100, (additionalUsers.length / 4) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                    {additionalUsers.length < 4 && (
                      <button
                        onClick={() => setShowAddUserForm(!showAddUserForm)}
                        className="inline-flex items-center justify-center rounded-lg border border-transparent bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {showAddUserForm ? 'Hide form' : 'Add user'}
                      </button>
                    )}
                  </div>

                  {showAddUserForm && (
                    <div className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <h5 className="text-sm font-semibold text-gray-900">Send invitation</h5>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Full name</label>
                          <input
                            type="text"
                            value={newUserForm.name}
                            onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Enter full name"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium uppercase tracking-wide text-gray-500">Email address</label>
                          <input
                            type="email"
                            value={newUserForm.email}
                            onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                            placeholder="teammate@example.com"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => {
                            setShowAddUserForm(false);
                            setNewUserForm({ name: '', email: '' });
                          }}
                          className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddUser}
                          disabled={invitingUser || !newUserForm.name.trim() || !newUserForm.email.trim()}
                          className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:opacity-50"
                        >
                          {invitingUser ? 'Sending…' : 'Send invite'}
                        </button>
                      </div>
                    </div>
                  )}

                  {additionalUsersLoading ? (
                    <p className="text-sm text-gray-500">Loading additional users…</p>
                  ) : additionalUsers.length === 0 ? (
                    <div className="space-y-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 p-6 text-center">
                      <Users className="mx-auto h-8 w-8 text-gray-400" />
                      <h5 className="text-sm font-semibold text-gray-900">No additional users yet</h5>
                      <p className="text-sm text-gray-600">Invite your team to collaborate on invoices, chat, and payments.</p>
                      <button
                        onClick={() => setShowAddUserForm(true)}
                        className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add your first user
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {additionalUsers.map((additionalUser) => {
                        const statusNormalized = String(additionalUser.status || '').toLowerCase();
                        const isPending = ['invited', 'pending invite', 'pending'].includes(statusNormalized);
                        const statusLabel = statusNormalized === 'active'
                          ? 'Active'
                          : isPending
                          ? 'Pending invite'
                          : additionalUser.status || 'Unknown';
                        const statusClass = statusNormalized === 'active'
                          ? 'bg-green-100 text-green-700'
                          : isPending
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-700';
                        const invitedDate = formatDateDisplay(additionalUser.invitedAt || additionalUser.createdAt);

                        return (
                          <div key={additionalUser.id} className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-start justify-between">
                              <div className="space-y-1 text-left">
                                <p className="text-sm font-semibold text-gray-900">{additionalUser.name || 'Pending user'}</p>
                                <p className="text-xs text-gray-500 break-all">{additionalUser.email}</p>
                              </div>
                              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                                {statusLabel}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500 space-y-1">
                              <p>Role: {additionalUser.role || 'User'}</p>
                              <p>Invited: {invitedDate || '—'}</p>
                            </div>
                            <div className="flex items-center justify-end space-x-2">
                              {isPending && (
                                <button
                                  onClick={() => handleResendInvite(additionalUser.id)}
                                  disabled={resendingUserId === additionalUser.id}
                                  className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                >
                                  {resendingUserId === additionalUser.id ? (
                                    <>
                                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                                      Resending…
                                    </>
                                  ) : (
                                    <>
                                      <RefreshCw className="mr-2 h-4 w-4" />
                                      Resend invite
                                    </>
                                  )}
                                </button>
                              )}
                              <button
                                onClick={() => handleRemoveUser(additionalUser.id)}
                                disabled={removingUserId === additionalUser.id}
                                className="inline-flex items-center rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                              >
                                {removingUserId === additionalUser.id ? (
                                  <>
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Removing…
                                  </>
                                ) : (
                                  <>
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Remove
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </SettingSection>
        )}
      </div>
    </div>
      {renderPlanChangeModal()}
      {renderCancelModal()}
      {showPaymentMethodModal && (
        <PaymentMethodSetupModal
          isOpen={showPaymentMethodModal}
          onClose={() => setShowPaymentMethodModal(false)}
          onSuccess={async () => {
            setShowPaymentMethodModal(false);
            setPaymentMethodLoading(true);
            try {
              // Add small delay to ensure database write is committed
              await new Promise(resolve => setTimeout(resolve, 500));
              const response = await api.get('/api/payment-methods/default');
              if (response.data.success) {
                setDefaultPaymentMethod(response.data.data);
                setMessage({ type: 'success', text: 'Payment method set up successfully!' });
              } else {
                // If still not found, try again after a longer delay
                await new Promise(resolve => setTimeout(resolve, 1000));
                const retryResponse = await api.get('/api/payment-methods/default');
                if (retryResponse.data.success) {
                  setDefaultPaymentMethod(retryResponse.data.data);
                  setMessage({ type: 'success', text: 'Payment method set up successfully!' });
                }
              }
            } catch (error) {
              console.error('Error refreshing payment method:', error);
              // Don't show error if payment method was just set up - might be a timing issue
              if (error.response?.status !== 404) {
                setMessage({ type: 'warning', text: 'Payment method saved but refresh failed. Please reload the page.' });
              }
            } finally {
              setPaymentMethodLoading(false);
            }
          }}
        />
      )}
    </>
  );
};

// Payment Method Setup Modal Component - Mobile Optimized
const PaymentMethodSetupModal = ({ isOpen, onClose, onSuccess }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [setupIntentData, setSetupIntentData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && !clientSecret && !loading) {
      // Create SetupIntent when modal opens
      const createSetupIntent = async () => {
        try {
          setLoading(true);
          setError(null);
          const response = await api.post('/api/payment-methods/setup-intent');
          
          if (response.data.success) {
            setClientSecret(response.data.data.clientSecret);
            setSetupIntentData(response.data.data);
          } else {
            setError(response.data.error || 'Failed to initialize payment method setup');
          }
        } catch (err) {
          console.error('Error creating SetupIntent:', err);
          setError(err.response?.data?.error || 'Failed to initialize payment method setup');
        } finally {
          setLoading(false);
        }
      };
      
      createSetupIntent();
    } else if (!isOpen) {
      // Reset when modal closes
      setClientSecret(null);
      setSetupIntentData(null);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, clientSecret, loading]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center md:p-4 p-0">
      <div className="bg-white md:rounded-xl rounded-none shadow-2xl md:w-full md:max-w-md w-full h-full md:max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h3 className="text-lg md:text-xl font-semibold text-gray-900">Set Up Payment Method</h3>
            <button
              onClick={onClose}
              disabled={processing || loading}
              className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 p-1"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4 md:mb-6">
            Add a payment method that will be used for "Pay Now" and "Pay on Due Date" when paying invoices.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400 mb-4" />
              <p className="text-sm text-gray-600">Initializing payment form...</p>
            </div>
          ) : clientSecret ? (
            <Elements 
              stripe={stripePromise} 
              options={{ 
                clientSecret,
                appearance: {
                  theme: 'stripe',
                }
              }}
              onLoadError={(error) => {
                console.error('❌ Elements provider load error:', error);
                console.error('❌ Elements error keys:', Object.keys(error || {}));
              }}
            >
              <PaymentMethodForm
                clientSecret={clientSecret}
                setupIntentData={setupIntentData}
                onSuccess={onSuccess}
                onClose={onClose}
                onError={(err) => setError(err)}
              />
            </Elements>
          ) : error ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
              <p className="text-sm text-red-600 mb-4">{error}</p>
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// Inner form component that uses Stripe hooks
const PaymentMethodForm = ({ clientSecret, setupIntentData, onSuccess, onClose, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      onError('Stripe is not loaded. Please wait a moment and try again.');
      return;
    }

    setProcessing(true);
    onError(null);

    try {
      // Confirm SetupIntent
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required'
      });

      if (confirmError) {
        onError(confirmError.message || 'Failed to save payment method. Please try again.');
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded' && setupIntent.payment_method) {
        // Save as default payment method
        // CRITICAL: Payment method is on PLATFORM account (SetupIntent was created on platform)
        // The payment method ID from setupIntent.payment_method is the platform payment method ID
        // setupIntentData.customerId is the platform customer ID
        const saveResponse = await api.post('/api/payment-methods/default', {
          stripePaymentMethodId: setupIntent.payment_method, // Platform payment method ID
          connectedAccountId: setupIntentData.connectedAccountId, // User's connected account (where we'll clone it)
          platformCustomerId: setupIntentData.customerId || setupIntentData.platformCustomerId // Platform customer ID
        });

        if (saveResponse.data.success) {
          setProcessing(false);
          onSuccess();
          onClose();
        } else {
          onError(saveResponse.data.error || 'Payment method saved but failed to set as default. Please try again.');
          setProcessing(false);
        }
      } else {
        onError('Setup incomplete. Please try again.');
        setProcessing(false);
      }
    } catch (err) {
      console.error('Error setting up payment method:', err);
      onError(err.response?.data?.error || 'An unexpected error occurred. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="border border-gray-200 rounded-lg p-4">
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
            fields: {
              billingDetails: 'auto'
            }
          }}
          onLoadError={(error) => {
            console.error('❌ PaymentElement load error:', error);
            const errorKeys = Object.keys(error || {});
            console.error('❌ Error object keys:', errorKeys);
            
            // Log each key and its value
            errorKeys.forEach(key => {
              console.error(`❌ Error.${key}:`, error[key]);
            });
            
            // Try to extract error message from various possible structures
            let errorMessage = 'Failed to load payment form.';
            if (error?.message) {
              errorMessage = error.message;
            } else if (error?.type) {
              errorMessage = `Error type: ${error.type}`;
            } else if (error?.code) {
              errorMessage = `Error code: ${error.code}`;
            } else if (typeof error === 'string') {
              errorMessage = error;
            } else if (errorKeys.length > 0) {
              // If we have keys but no standard properties, show the first key's value
              const firstKey = errorKeys[0];
              errorMessage = `Error: ${firstKey} = ${JSON.stringify(error[firstKey])}`;
            }
            
            console.error('❌ ClientSecret preview:', clientSecret ? `${clientSecret.substring(0, 30)}...` : 'missing');
            console.error('❌ SetupIntent ID from clientSecret:', clientSecret ? clientSecret.split('_secret_')[0] : 'missing');
            
            onError(errorMessage);
          }}
          onReady={() => {
            console.log('✅ PaymentElement loaded successfully');
          }}
        />
      </div>

      <div className="flex items-center space-x-2 text-xs text-gray-500">
        <Shield className="w-4 h-4 flex-shrink-0" />
        <span>Your payment information is secure and encrypted</span>
      </div>

      <div className="flex flex-col md:flex-row gap-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          disabled={processing}
          className="w-full md:flex-1 px-4 py-3 md:py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="w-full md:flex-1 px-4 py-3 md:py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
        >
          {processing ? 'Saving...' : 'Save Payment Method'}
        </button>
      </div>
    </form>
  );
};

export default MobileSettings;

